/**
 * Las facturas de otro programa — `OD-0057` D-03 y D-06.
 *
 * Una copia de CONSULTA de lo que se emitió con el programa anterior: se lee y
 * se busca, nada más. No hay botón de editar, rectificar, anular ni enviar,
 * porque la API no los tiene (D-06: «NO desde Faktelio en V1»). Los importes
 * llegan escritos de la API: la web no suma nada.
 *
 * Con `clienteId`, la lista de un cliente, para su ficha.
 */
import { useEffect, useState } from "react";

import { apiImportacion, type FacturaImportada, type LineaFacturaImportada } from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible } from "../ui/formato";
import { Aviso, Boton, Campo, FilaPulsable } from "../ui/primitivas";

function fechaLegible(dia: string): string {
  const [a, m, d] = dia.split("-");
  return `${d}/${m}/${a}`;
}

function Ficha({ id, onVolver }: { readonly id: string; readonly onVolver: () => void }) {
  const [datos, setDatos] = useState<{ readonly factura: FacturaImportada; readonly lineas: readonly LineaFacturaImportada[] } | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    void apiImportacion.factura(id).then((r) => (r.ok ? setDatos(r.datos) : setError(true)));
  }, [id]);
  const f = datos?.factura;
  return (
    <section className="tarjeta" aria-label={t.facturasImportadas.titulo}>
      <div className="acciones">
        <Boton variante="terciario" onClick={onVolver}>
          {t.facturasImportadas.fichaVolver}
        </Boton>
      </div>
      {error ? <Aviso tono="error">{t.facturasImportadas.error}</Aviso> : null}
      {f === undefined ? null : (
        <>
          <h3>{f.numero}</h3>
          <Aviso tono="info">{t.facturasImportadas.copia}</Aviso>
          <ul className="lista-filas">
            <li className="fila">
              <span>{t.facturasImportadas.fecha}</span>
              <span>{fechaLegible(f.fecha)}</span>
            </li>
            {f.fechaOperacion !== null ? (
              <li className="fila">
                <span>{t.facturasImportadas.fechaOperacion}</span>
                <span>{fechaLegible(f.fechaOperacion)}</span>
              </li>
            ) : null}
            <li className="fila">
              <span>{t.facturasImportadas.cliente}</span>
              <span>{f.cliente ?? t.facturasImportadas.sinCliente}</span>
            </li>
            {f.nifCliente !== null ? (
              <li className="fila">
                <span>{t.facturasImportadas.nif}</span>
                <span>{f.nifCliente}</span>
              </li>
            ) : null}
            <li className="fila">
              <span>{t.facturasImportadas.programa}</span>
              <span>{f.programa ?? t.facturasImportadas.otroPrograma}</span>
            </li>
            <li className="fila">
              <span>{t.importar.campos.kind}</span>
              <span>{t.facturasImportadas.tipos[f.tipo] ?? f.tipo}</span>
            </li>
          </ul>
          <table className="tabla">
            <thead>
              <tr>
                <th>{t.facturasImportadas.base}</th>
                <th>{t.facturasImportadas.tipoIva}</th>
                <th>{t.facturasImportadas.cuota}</th>
              </tr>
            </thead>
            <tbody>
              {(datos?.lineas ?? []).map((l, i) => (
                <tr key={i}>
                  <td>{t.facturasImportadas.importe(importeLegible(l.base))}</td>
                  <td>{l.tipoIva.replace(/,00$/, "")} %</td>
                  <td>{t.facturasImportadas.importe(importeLegible(l.iva))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="lista-filas">
            {f.recargo !== "0,00" ? (
              <li className="fila">
                <span>{t.facturasImportadas.recargo}</span>
                <span>{t.facturasImportadas.importe(importeLegible(f.recargo))}</span>
              </li>
            ) : null}
            {f.retencion !== "0,00" ? (
              <li className="fila">
                <span>{t.facturasImportadas.retencion}</span>
                <span>{t.facturasImportadas.importe(importeLegible(`-${f.retencion}`))}</span>
              </li>
            ) : null}
            <li className="fila">
              <span className="fila-titulo">{t.facturasImportadas.totalFactura}</span>
              <span className="fila-titulo">{t.facturasImportadas.importe(importeLegible(f.total))}</span>
            </li>
          </ul>
          <p className="ayuda-neutra">{t.facturasImportadas.importadaEl(fechaLegible(f.importadaEl.slice(0, 10)))}</p>
        </>
      )}
    </section>
  );
}

export function PantallaFacturasImportadas({ clienteId = null, onVolver }: { readonly clienteId?: string | null; readonly onVolver?: () => void }) {
  const [q, setQ] = useState("");
  const [lista, setLista] = useState<{ readonly facturas: readonly FacturaImportada[]; readonly total: string } | null>(null);
  const [error, setError] = useState(false);
  const [abierta, setAbierta] = useState<string | null>(null);

  useEffect(() => {
    const espera = setTimeout(() => {
      void apiImportacion.facturas({ q, cliente: clienteId }).then((r) => {
        if (r.ok) {
          setLista(r.datos);
          setError(false);
        } else setError(true);
      });
    }, 250);
    return () => clearTimeout(espera);
  }, [q, clienteId]);

  if (abierta !== null) return <Ficha id={abierta} onVolver={() => setAbierta(null)} />;

  // En la ficha de un cliente sin facturas importadas no se pinta nada.
  if (clienteId !== null && (lista === null || lista.facturas.length === 0)) return null;

  return (
    <section className={clienteId === null ? "" : "tarjeta"} aria-label={t.facturasImportadas.titulo}>
      {clienteId === null ? (
        <>
          {onVolver !== undefined ? (
            <div className="acciones">
              <Boton variante="terciario" onClick={onVolver}>
                {t.facturasImportadas.volver}
              </Boton>
            </div>
          ) : null}
          <h2 className="titulo-seccion">{t.facturasImportadas.titulo}</h2>
          <p className="ayuda-neutra">{t.facturasImportadas.detalle}</p>
          <Campo id="importadas-buscar" etiqueta={t.facturasImportadas.buscar} valor={q} onChange={setQ} requerido={false} />
        </>
      ) : (
        <h2>{t.facturasImportadas.enCliente}</h2>
      )}
      {error ? <Aviso tono="error">{t.facturasImportadas.error}</Aviso> : null}
      {lista !== null && lista.facturas.length === 0 ? <p className="ayuda-neutra">{t.facturasImportadas.vacio}</p> : null}
      {lista !== null && lista.facturas.length > 0 ? (
        <>
          <p className="ayuda-neutra">{t.facturasImportadas.total(lista.facturas.length, importeLegible(lista.total))}</p>
          <ul className="lista-filas">
            {lista.facturas.map((f) => (
              <FilaPulsable key={f.id} onClick={() => setAbierta(f.id)}>
                <span className="fila-titulo">
                  {f.numero} · {t.facturasImportadas.importe(importeLegible(f.total))}
                </span>
                <span>
                  {fechaLegible(f.fecha)} · {f.cliente ?? t.facturasImportadas.sinCliente} · {f.programa ?? t.facturasImportadas.otroPrograma}
                </span>
              </FilaPulsable>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
